import React, { useRef, useState } from "react";
import { Tooltip } from "@material-ui/core";
import { InlineEditWrap, KeyLabel, StatusChip, StyledTd, StyledTr, EmptyTextHint } from "../styles";
import TagsCell from "./TagsCell";
import ValidationBadge from "./ValidationBadge";
import { validateEntry } from "../validators";
import type { GlossaryTerm, StationsTextEntry } from "../types";
import { LABELS } from "../labels";

interface Props {
	entry: StationsTextEntry & { tags?: string[] };
	selected: boolean;
	active: boolean;
	glossary: GlossaryTerm[];
	onToggleSelect: () => void;
	onRowClick: () => void;
	onInlineEdit: (value: string) => void;
	langs: string[];
	onInlineEditTranslation: (lang: string, value: string) => void;
	onAddTag: (tag: string) => void;
	onRemoveTag: (tag: string) => void;
	onFilterByTag: (tag: string) => void;
}

export default function TableRow({
	entry,
	selected,
	active,
	glossary,
	onToggleSelect,
	onRowClick,
	onInlineEdit,
	langs,
	onInlineEditTranslation,
	onAddTag,
	onRemoveTag,
	onFilterByTag,
}: Props) {
	const [editing, setEditing] = useState(false);
	const [editValue, setEditValue] = useState(entry.currentText);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const validations = validateEntry(entry as StationsTextEntry, glossary);
	const tags = entry.tags || [];

	function handleTextClick(e: React.MouseEvent) {
		e.stopPropagation();
		setEditing(true);
		setEditValue(entry.currentText);
		setTimeout(() => textareaRef.current?.focus(), 0);
	}

	function handleBlur() {
		setEditing(false);
		if (editValue !== entry.currentText) {
			onInlineEdit(editValue);
		}
	}

	function handleKeyDown(e: React.KeyboardEvent) {
		if (e.key === "Escape") {
			setEditing(false);
			setEditValue(entry.currentText);
		}
	}

	const [editingLang, setEditingLang] = useState<string | null>(null);
	const [editValueLang, setEditValueLang] = useState("");
	const textareaLangRef = useRef<HTMLTextAreaElement>(null);

	function translationOf(lang: string) {
		return String(entry.translations?.[lang] || "");
	}

	function handleLangClick(lang: string, e: React.MouseEvent) {
		e.stopPropagation();
		setEditingLang(lang);
		setEditValueLang(translationOf(lang));
		setTimeout(() => textareaLangRef.current?.focus(), 0);
	}

	function handleLangBlur(lang: string) {
		setEditingLang(null);
		if (editValueLang !== translationOf(lang)) {
			onInlineEditTranslation(lang, editValueLang);
		}
	}

	function handleLangKeyDown(lang: string, e: React.KeyboardEvent) {
		if (e.key === "Escape") {
			setEditingLang(null);
			setEditValueLang(translationOf(lang));
		}
	}

	function formatDate(iso: string | null | undefined): string {
		if (!iso) return "—";
		try {
			return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
		} catch {
			return "—";
		}
	}

	return (
		<StyledTr
			selected={selected}
			active={active}
			onClick={onRowClick}>
			<StyledTd onClick={(e) => e.stopPropagation()}>
				<input
					type="checkbox"
					checked={selected}
					onChange={onToggleSelect}
				/>
			</StyledTd>
			<StyledTd>
				<Tooltip
					title={entry.key}
					placement="top">
					<KeyLabel>{entry.key}</KeyLabel>
				</Tooltip>
			</StyledTd>
			<StyledTd>{entry.screen}</StyledTd>
			<StyledTd>{entry.group}</StyledTd>
			<StyledTd onClick={handleTextClick}>
				{editing ? (
					<InlineEditWrap onClick={(e) => e.stopPropagation()}>
						<textarea
							ref={textareaRef}
							rows={2}
							value={editValue}
							onChange={(e) => setEditValue(e.target.value)}
							onBlur={handleBlur}
							onKeyDown={handleKeyDown}
						/>
					</InlineEditWrap>
				) : entry.currentText ? (
					<span style={{ cursor: "text", display: "block", minHeight: 20 }}>{entry.currentText}</span>
				) : entry.defaultText ? (
					<span style={{ cursor: "text", display: "block", minHeight: 20, color: "#475569" }}>
						{entry.defaultText}
						<EmptyTextHint title="Chưa có bản dịch — đang hiển thị text gốc từ code">default</EmptyTextHint>
					</span>
				) : (
					<span
						style={{
							cursor: "text",
							display: "block",
							minHeight: 20,
							color: "#94a3b8",
							fontStyle: "italic",
						}}>
						(empty)
					</span>
				)}
			</StyledTd>
			{langs.map((lang) => (
				<StyledTd
					key={lang}
					onClick={(e) => handleLangClick(lang, e)}>
					{editingLang === lang ? (
						<InlineEditWrap onClick={(e) => e.stopPropagation()}>
							<textarea
								ref={textareaLangRef}
								rows={2}
								lang={lang}
								value={editValueLang}
								onChange={(e) => setEditValueLang(e.target.value)}
								onBlur={() => handleLangBlur(lang)}
								onKeyDown={(e) => handleLangKeyDown(lang, e)}
							/>
						</InlineEditWrap>
					) : translationOf(lang) ? (
						<span style={{ cursor: "text", display: "block", minHeight: 20 }}>{translationOf(lang)}</span>
					) : (
						<span
							style={{
								cursor: "text",
								display: "block",
								minHeight: 20,
								color: "#94a3b8",
								fontStyle: "italic",
							}}>
							<EmptyTextHint title={LABELS.viMissingHint}>chưa dịch</EmptyTextHint>
						</span>
					)}
				</StyledTd>
			))}
			<StyledTd onClick={(e) => e.stopPropagation()}>
				<TagsCell
					tags={tags}
					onAdd={onAddTag}
					onRemove={onRemoveTag}
					onFilterByTag={onFilterByTag}
				/>
			</StyledTd>
			<StyledTd>
				<StatusChip status={entry.status}>{entry.status}</StatusChip>
			</StyledTd>
			<StyledTd style={{ fontSize: "0.75rem", color: "#64748b" }}>{formatDate(entry.updatedAt)}</StyledTd>
			<StyledTd onClick={(e) => e.stopPropagation()}>
				<ValidationBadge results={validations} />
			</StyledTd>
		</StyledTr>
	);
}
